// Location Service - Yeni Location API'mizden lokasyon verilerini çeker
import { getApiBaseUrl } from '../config';

export interface Country {
  code: string;
  name: string;
}

export interface Region {
  code: string;
  name: string;
  countryCode: string;
}

export interface City {
  code: string;
  name: string;
  stateCode: string;
}

export interface District {
  code: string;
  name: string;
  cityCode: string;
}

export interface StoreLocation {
  storeCode: string;
  storeDescription: string;
  countryCode: string;
  countryName: string;
  stateCode: string;
  stateName: string;
  cityCode: string;
  cityName: string;
  districtCode: string;
  districtName: string;
}

class LocationService {
  private baseUrl: string;

  constructor() {
    // Geliştirmede: sayfa IP ile açıldıysa (örn. 10.38.0.118:3001) API aynı host:5001 kullanılır
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Tüm ülkeleri getirir
   */
  async getCountries(): Promise<Country[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Location/countries`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const countries: Country[] = await response.json();
      console.log('Ülkeler başarıyla yüklendi:', countries.length);
      return countries;
    } catch (error) {
      console.error('Ülkeler yüklenirken hata:', error);
      throw error;
    }
  }

  /**
   * Belirtilen ülke koduna göre bölgeleri getirir
   */
  async getRegions(countryCode: string): Promise<Region[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Location/regions/${countryCode}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const regions: Region[] = await response.json();
      console.log(`${countryCode} için bölgeler yüklendi:`, regions.length);
      return regions;
    } catch (error) {
      console.error(`${countryCode} bölgeleri yüklenirken hata:`, error);
      throw error;
    }
  }

  /**
   * Belirtilen bölge koduna göre şehirleri getirir
   */
  async getCities(stateCode: string): Promise<City[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Location/cities/${stateCode}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const cities: City[] = await response.json();
      console.log(`${stateCode} için şehirler yüklendi:`, cities.length);
      return cities;
    } catch (error) {
      console.error(`${stateCode} şehirleri yüklenirken hata:`, error);
      throw error;
    }
  }

  /**
   * Belirtilen şehir koduna göre ilçeleri getirir
   */
  async getDistricts(cityCode: string): Promise<District[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Location/districts/${cityCode}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const districts: District[] = await response.json();
      console.log(`${cityCode} için ilçeler yüklendi:`, districts.length);
      return districts;
    } catch (error) {
      console.error(`${cityCode} ilçeleri yüklenirken hata:`, error);
      throw error;
    }
  }

  /**
   * Store koduna göre varsayılan lokasyon bilgilerini getirir
   */
  async getStoreDefaultLocation(storeCode: string): Promise<StoreLocation | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Location/store-default/${storeCode}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`Store ${storeCode} için varsayılan lokasyon bulunamadı`);
          return null;
        }
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const storeLocation: StoreLocation = await response.json();
      console.log(`${storeCode} için varsayılan lokasyon yüklendi:`, storeLocation);
      return storeLocation;
    } catch (error) {
      console.error(`${storeCode} varsayılan lokasyonu yüklenirken hata:`, error);
      throw error;
    }
  }

  /**
   * Ülke kodunu ülke adından bulur
   */
  findCountryCodeByCountryName(countries: Country[], countryName: string): string | null {
    const country = countries.find(c => c.name === countryName);
    return country ? country.code : null;
  }

  /**
   * Bölge kodunu bölge adından bulur
   */
  findRegionCodeByRegionName(regions: Region[], regionName: string): string | null {
    const region = regions.find(r => r.name === regionName);
    return region ? region.code : null;
  }

  /**
   * Şehir adından kod bulma (daha toleranslı arama)
   */
  findCityCodeByCityName(cities: City[], cityName: string): string | null {
    if (!cityName || !cities || cities.length === 0) return null;
    
    const normalizedCityName = cityName.trim().toLowerCase();
    
    // Tam eşleşme ara
    const exactMatch = cities.find(c => 
      (c.name || '').trim().toLowerCase() === normalizedCityName
    );
    if (exactMatch) return exactMatch.code;
    
    // Kısmi eşleşme ara
    const partialMatch = cities.find(c => 
      (c.name || '').trim().toLowerCase().includes(normalizedCityName) ||
      normalizedCityName.includes((c.name || '').trim().toLowerCase())
    );
    if (partialMatch) return partialMatch.code;
    
    return null;
  }

  /**
   * İlçe adından kod bulma (daha toleranslı arama)
   */
  findDistrictCodeByDistrictName(districts: District[], districtName: string): string | null {
    if (!districtName || !districts || districts.length === 0) return null;
    
    const normalizedDistrictName = districtName.trim().toLowerCase();
    
    // Tam eşleşme ara
    const exactMatch = districts.find(d => 
      (d.name || '').trim().toLowerCase() === normalizedDistrictName
    );
    if (exactMatch) return exactMatch.code;
    
    // Kısmi eşleşme ara
    const partialMatch = districts.find(d => 
      (d.name || '').trim().toLowerCase().includes(normalizedDistrictName) ||
      normalizedDistrictName.includes((d.name || '').trim().toLowerCase())
    );
    if (partialMatch) return partialMatch.code;
    
    return null;
  }

  /**
   * Seçilen location bilgilerinden kodları çıkarır
   */
  extractLocationCodes(
    selectedCountry: string,
    selectedRegion: string, 
    selectedCity: string,
    selectedDistrict: string,
    countries: Country[],
    regions: Region[],
    cities: City[],
    districts: District[]
  ): {
    countryCode: string;
    stateCode: string;
    cityCode: string;
    districtCode: string;
  } {
    const countryCode = this.findCountryCodeByCountryName(countries, selectedCountry) || '';
    const cityCode = this.findCityCodeByCityName(cities, selectedCity) || '';
    const districtCode = this.findDistrictCodeByDistrictName(districts, selectedDistrict) || '';
    
    // StateCode'u seçilen city'den al
    let stateCode = '';
    if (cityCode) {
      const selectedCity = cities.find(c => c.code === cityCode);
      stateCode = selectedCity?.stateCode || '';
    }
    
    return {
      countryCode,
      stateCode,
      cityCode,
      districtCode
    };
  }
}

// Singleton instance
export const locationService = new LocationService();
